/**
 * 🛡️ G2GENTE - ARQUIVO PROTEGIDO | SELO: G2GENTE-PROTECTED-2026-02-24-V1
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { chatAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  ArrowLeft,
  Loader2,
  MessageSquare,
  Send,
  Building2,
  User,
  Check,
  CheckCheck
} from 'lucide-react';
import { toast } from 'sonner';

// Chat List Component
export function MobileChats() {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchChats();
  }, []);

  const fetchChats = async () => {
    try {
      const response = await chatAPI.getChats();
      setChats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar conversas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="mobile-chats">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-slate-200">
        <div className="flex items-center gap-3 p-4">
          <button onClick={() => navigate('/m')} className="text-slate-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-bold text-slate-900">Mensagens</h1>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : chats.length === 0 ? (
          <div className="text-center py-20">
            <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-900 mb-2">Nenhuma conversa</h3>
            <p className="text-sm text-slate-600">
              Após aprovar uma solicitação, o chat será habilitado.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {chats.map((chat) => (
              <Card 
                key={chat.id} 
                className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow active:scale-[0.99]"
                onClick={() => navigate(`/m/chats/${chat.id}`)}
                data-testid={`mobile-chat-${chat.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">
                        {chat.company_name || 'Empresa'}
                      </h3>
                      <p className="text-sm text-slate-500">Toque para conversar</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Chat Room Component
export function MobileChatRoom() {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [chatInfo, setChatInfo] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000); // Poll for new messages
    return () => clearInterval(interval);
  }, [chatId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const response = await chatAPI.getMessages(chatId);
      setMessages(response.data);
      
      // Get chat info
      const chatsRes = await chatAPI.getChats();
      const chat = chatsRes.data.find(c => c.id === chatId);
      setChatInfo(chat);
    } catch (error) {
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await chatAPI.sendMessage(chatId, newMessage);
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col" data-testid="mobile-chat-room">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-slate-200">
        <div className="flex items-center gap-3 p-4">
          <button onClick={() => navigate('/m/chats')} className="text-slate-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
            <Building2 className="w-5 h-5 text-cyan-600" />
          </div>
          <div>
            <h1 className="font-semibold text-slate-900">
              {chatInfo?.company_name || 'Empresa'}
            </h1>
            <p className="text-xs text-slate-500">Online</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-slate-500 text-sm">Nenhuma mensagem ainda</p>
            <p className="text-slate-400 text-xs">Inicie a conversa!</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMe = msg.sender_role === user?.role;
            return (
              <div
                key={msg.id}
                className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                    isMe
                      ? 'bg-cyan-600 text-white rounded-br-sm'
                      : 'bg-white text-slate-900 rounded-bl-sm shadow-sm'
                  }`}
                >
                  <p className="text-sm">{msg.message}</p>
                  <div className={`flex items-center justify-end gap-1 mt-1 ${isMe ? 'text-cyan-100' : 'text-slate-400'}`}>
                    <span className="text-xs">
                      {new Date(msg.created_at).toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    {isMe && (
                      msg.read ? (
                        <CheckCheck className="w-4 h-4 text-cyan-200" />
                      ) : (
                        <Check className="w-4 h-4 text-cyan-200" />
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="bg-white border-t border-slate-200 p-4">
        <div className="flex gap-2">
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Digite sua mensagem..."
            className="flex-1"
            disabled={sending}
            data-testid="mobile-chat-input"
          />
          <Button 
            type="submit"
            className="bg-cyan-600 hover:bg-cyan-700 px-4"
            disabled={sending || !newMessage.trim()}
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
